import { useMemo } from 'react'

import { waitForStableSession } from '../../lib/wait-for-stable-session'
import { useProgressiveAuth, ProgressiveAuthProvider } from './progressive-auth-context'
import {
  SessionAwareAuthenticator,
  type SessionAwareAuthenticatorProps
} from './session-aware-authenticator'
import type { SignInInput } from 'aws-amplify/auth'

function PasskeyAwareAuthenticatorInner({ services, ...props }: SessionAwareAuthenticatorProps) {
  const progressiveAuth = useProgressiveAuth()

  const mergedServices = useMemo(() => {
    return {
      ...services,
      async handleSignIn(formData: SignInInput) {
        const cleanedUsername = formData.username?.toLowerCase().trim().replace(/\s+/g, '') ?? ''
        const cleanedPassword = formData.password?.trim() ?? ''
        const { signIn, confirmSignIn } = await import('aws-amplify/auth')

        if (progressiveAuth.isChallengeReady) {
          try {
            let result

            if (progressiveAuth.nextStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
              result = await confirmSignIn({
                challengeResponse: 'PASSWORD'
              })
            }

            if (!result || result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
              result = await confirmSignIn({
                challengeResponse: cleanedPassword
              })
            }

            progressiveAuth.resetChallenges()
            await waitForStableSession()
            return result
          } catch (error) {
            console.warn('Falling back to regular sign-in', error)
            progressiveAuth.resetChallenges()
          }
        }

        progressiveAuth.resetChallenges()
        const res = await signIn({ username: cleanedUsername, password: cleanedPassword })
        await waitForStableSession()
        return res
      }
    }
  }, [progressiveAuth, services])

  return <SessionAwareAuthenticator {...props} services={mergedServices} />
}

export function PasskeyAwareAuthenticator(props: SessionAwareAuthenticatorProps) {
  return (
    <ProgressiveAuthProvider>
      <PasskeyAwareAuthenticatorInner {...props} />
    </ProgressiveAuthProvider>
  )
}
